import { useAIChatbotAccess } from "@faire/help-center-self-serve/hooks/useAIChatbotAccess";
import { SEARCH_SECTION_FROM } from "@faire/help-center-utils/consts/eventEnum";
import { CustomerTypes } from "@faire/help-center-utils/enums/CustomerType";
import { useCustomerType } from "@faire/help-center-utils/hooks/useCustomerType";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { CommonTopics } from "../CommonTopics/CommonTopics";
import { RoundSearchInput } from "../SearchField/RoundSearchInput";
import { Toggle } from "../Toggle/Toggle";

import { HeaderTitle } from "./HeaderTitle";

const RETAILER_BACKGROUND_IMAGE =
  "https://cdn.faire.com/fastly/d8e23556d1bcf0d6c9a3717b879a9f673303738ce91dfbd450bd5c90a3c3e953.png";
const BRAND_BACKGROUND_IMAGE =
  "https://cdn.faire.com/fastly/b4160bf066382500f2ea41cf137fcee1cdd143562c948aa7c39b45042f94063a.png";

export const HeaderBanner: React.FC = () => {
  const { customerType } = useCustomerType();
  const { isMobile } = useViewport();
  const { hasAIChatbotAccess } = useAIChatbotAccess();

  return (
    <HeaderBannerWrapper $customerType={customerType}>
      <Toggle />
      <Spacer height="3x" />
      <HeaderTitle />
      <Spacer height="3x" />
      <RoundSearchInput
        sectionFrom={SEARCH_SECTION_FROM.NAV_SEARCH_BANNER}
        id="main-search-bar"
      />
      <Spacer height="3x" />
      {hasAIChatbotAccess ? (
        <Typography>
          <StrictLocalMsg
            defaultMessage="For the fastest answer to most questions, <ChatbotLinkFormatting>try our virtual assistant</ChatbotLinkFormatting>."
            description={{
              text: "Banner message for fast help with the chatbot",
            }}
            values={{
              ChatbotLinkFormatting: OpenChatbotLink,
            }}
          />
        </Typography>
      ) : (
        <CommonTopics />
      )}
      {isMobile ? null : <Spacer height="1x" />}
      <Spacer height="3x" />
      <Spacer height="1/2x" />
    </HeaderBannerWrapper>
  );
};

const HeaderBannerWrapper = styled(Column).attrs({
  align: { mobileAndAbove: "flex-start", tabletAndAbove: "center" },
})<{
  $customerType: CustomerTypes;
}>`
  background-image: url(${({ $customerType }) =>
    $customerType === CustomerTypes.RETAILER
      ? getBackgroundImage(RETAILER_BACKGROUND_IMAGE)
      : getBackgroundImage(BRAND_BACKGROUND_IMAGE)});

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("7x")};
  }
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("7x", "4x")};
  }
  background-size: cover;
  background-position: center center;
  transition: background-image 0.5s linear;
`;

const getBackgroundImage = (url: string) =>
  optimizeImageUrl(
    {
      url,
    },
    false,
    {
      resizeMethod: "fit",
    }
  );

const OpenChatbotLink = (chunks: React.ReactNode) => (
  <Button variant="tertiary" onClick={() => window.duet.open()}>
    {chunks}
  </Button>
);
